/**
 * The quick-search palette: ⌘K / Ctrl-K anywhere in the signed-in app.
 *
 * Ranking lives in `lib/search-model.js` (pure, tested against arrays); this
 * file is the chrome around it — the shortcut, the overlay, the fetch-once
 * cache, and the arrow-key loop. In `ui/` because it belongs to no feature
 * and a feature may not import another (ARCHITECTURE.md §3); the overlay
 * reuses `ui/modal-shell.js`, so it has the same three ways out as every
 * other dialogue: the ×, a click on the dim area, and Escape.
 *
 * ── What opening costs ────────────────────────────────────────────────────
 *
 * Nothing until the FIRST open, then three parallel reads: the trip index
 * (ONE document however many trips — `trip-index.js`), the clients
 * collection, and the leads collection. Cached for the session and refetched
 * only when the palette is opened again 5+ minutes later, so hammering ⌘K
 * costs nothing. A missing index is served as an empty trip list rather than
 * rebuilt — a rebuild is N reads, and the dashboard (the landing screen,
 * which every session passes through) already rebuilds it.
 *
 * ── The shortcut's one rule ───────────────────────────────────────────────
 *
 * ⌘K inside an input, textarea or contenteditable does NOTHING, except in
 * the palette's own box. An advisor mid-sentence in a client email who
 * reaches for a browser shortcut must not have the app eat the keystroke and
 * throw an overlay over their draft.
 */

import { h, clear } from '../lib/dom.js'
import { modalClose, overlayCloser, closeOnEscape, lockScroll } from './modal-shell.js'
import { searchGroups, tripItem, clientItem, leadItem } from '../lib/search-model.js'
import { readCollection } from '../lib/firebase.js'
import { loadTripIndex } from '../lib/trip-index.js'
import { goTo, tripUrl } from '../lib/navigate.js'
import { appUrl } from '../lib/app-base.js'

/** @typedef {import('../lib/search-model.js').SearchItem} SearchItem */

/** Five minutes. Fresh enough for a search; a session's writes go through
    screens that show their own live lists anyway. */
export const REFRESH_MS = 5 * 60 * 1000

/**
 * Where selecting a result goes. Trips open their editor; clients and leads
 * open their screens, which is where legacy lands you too — neither has a
 * per-record URL yet, and inventing one here would be a link to nowhere.
 *
 * @param {SearchItem} item
 * @returns {string}
 */
export function hrefFor(item) {
  if (item.kind === 'trip') return tripUrl(item.id)
  if (item.kind === 'client') return appUrl('/clients')
  return appUrl('/leads')
}

/**
 * The one fetch, all three sources in parallel. Each source fails soft — a
 * permissions hiccup on leads must not blank the trips the advisor can see —
 * but all three failing at once (offline, signed-out mid-flight) rejects, so
 * the palette shows its error line instead of caching emptiness for 5 min.
 *
 * @param {string} uid
 * @returns {Promise<{trips: SearchItem[], clients: SearchItem[], leads: SearchItem[]}>}
 */
export async function loadWorkspace(uid) {
  let failures = 0
  /** @type {<T>(p: Promise<T>, fallback: T) => Promise<T>} */
  const soft = (p, fallback) => p.catch(() => ((failures += 1), fallback))
  const [index, clients, leads] = await Promise.all([
    soft(loadTripIndex(uid), { status: 'missing', summaries: [], doc: null }),
    soft(readCollection(['users', uid, 'clients']), []),
    soft(readCollection(['forms', uid, 'leads']), []),
  ])
  if (failures === 3) throw new Error('quick search could not read anything')
  return {
    trips: (index.summaries || []).map(tripItem),
    clients: (clients || []).map(clientItem),
    /* A converted lead is a client now and a dismissed one was declined;
       surfacing either would point the advisor at a row the leads screen
       hides (`leads-model.js`). */
    leads: (leads || []).filter((l) => !l.converted && !l.dismissed).map(leadItem),
  }
}

/** Is this keystroke happening inside something that takes typing? */
function inEditor(/** @type {any} */ target) {
  if (!target || !target.tagName) return false
  const tag = String(target.tagName).toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return Boolean(target.isContentEditable)
}

/**
 * Mount the palette with everything injectable, which is what the tests use.
 * `attachCommandPalette` below is the same thing wired to the real app.
 *
 * @param {Object} deps
 * @param {() => Promise<{trips: SearchItem[], clients: SearchItem[], leads: SearchItem[]}>} deps.load
 * @param {(href: string) => void} deps.navigate
 * @param {() => number} [deps.now]
 * @param {Document} [deps.doc]
 * @returns {() => void} teardown — remove the listener and any open overlay
 */
export function createCommandPalette(deps) {
  const doc = deps.doc || globalThis.document
  const now = deps.now || (() => Date.now())

  /** @type {{trips: SearchItem[], clients: SearchItem[], leads: SearchItem[]}|null} */
  let data = null
  let fetchedAt = 0
  /** @type {Promise<any>|null} */
  let pending = null
  let failed = false

  /** @type {HTMLElement|null} */
  let overlay = null
  /** @type {HTMLElement|null} */
  let input = null
  /** @type {HTMLElement|null} */
  let results = null
  let undoEscape = () => {}
  let unlockScroll = () => {}
  let query = ''
  let selected = 0
  /** @type {SearchItem[]} */
  let flat = []

  /** Fetch if this session has nothing, or nothing recent. Never twice at once. */
  function ensureData() {
    if (pending) return
    if (data && now() - fetchedAt < REFRESH_MS) return
    failed = false
    pending = deps.load().then(
      (loaded) => {
        data = loaded
        fetchedAt = now()
        pending = null
        if (overlay) renderResults()
      },
      () => {
        /* Keep whatever an earlier fetch delivered — stale beats blank. */
        pending = null
        failed = !data
        if (overlay) renderResults()
      }
    )
  }

  /** @param {SearchItem} item */
  function pick(item) {
    close()
    deps.navigate(hrefFor(item))
  }

  /** One row. A real button: focusable, clickable, announced. */
  function row(/** @type {SearchItem} */ item, /** @type {number} */ index) {
    return h(
      'button',
      {
        type: 'button',
        class: `cmdk-item${index === selected ? ' is-active' : ''}`,
        role: 'option',
        id: `cmdk-opt-${index}`,
        'aria-selected': index === selected ? 'true' : 'false',
        onclick: () => pick(item),
      },
      h('span', { class: 'cmdk-item-title' }, item.title),
      item.detail ? h('span', { class: 'cmdk-item-detail' }, item.detail) : null
    )
  }

  function renderResults() {
    if (!results || !input) return
    const groups = data ? searchGroups(data, query) : []
    flat = groups.flatMap((group) => group.items)
    if (selected >= flat.length) selected = 0
    clear(results)
    input.setAttribute('aria-activedescendant', flat.length ? `cmdk-opt-${selected}` : '')

    if (!query.trim()) {
      results.appendChild(
        h('p', { class: 'cmdk-empty' }, 'Start typing to search trips, clients and leads')
      )
      return
    }
    if (pending && !data) {
      results.appendChild(h('p', { class: 'cmdk-empty' }, 'Loading…'))
      return
    }
    if (failed && !data) {
      results.appendChild(h('p', { class: 'cmdk-empty' }, 'Search could not load. Close and try again.'))
      return
    }
    if (!flat.length) {
      /* The query is echoed so a typo is visible where the advisor is looking. */
      results.appendChild(h('p', { class: 'cmdk-empty' }, `No matches for “${query.trim()}”`))
      return
    }

    let offset = 0
    for (const group of groups) {
      results.appendChild(h('div', { class: 'cmdk-group-label' }, group.label))
      group.items.forEach((item, i) => results.appendChild(row(item, offset + i)))
      offset += group.items.length
    }
  }

  /** Arrow keys move, Enter goes. Bound on the input, where focus lives. */
  function onInputKeys(/** @type {any} */ e) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      if (!flat.length) return
      const step = e.key === 'ArrowDown' ? 1 : -1
      selected = (selected + step + flat.length) % flat.length
      renderResults()
      return
    }
    if (e.key === 'Enter' && flat[selected]) {
      e.preventDefault()
      pick(flat[selected])
    }
  }

  function open() {
    if (overlay) return
    ensureData()
    query = ''
    selected = 0
    input = h('input', {
      class: 'cmdk-input',
      type: 'text',
      placeholder: 'Search trips, clients and leads…',
      'aria-label': 'Search trips, clients and leads',
      role: 'combobox',
      'aria-expanded': 'true',
      'aria-controls': 'cmdk-results',
      autocomplete: 'off',
      spellcheck: 'false',
      oninput: (/** @type {any} */ e) => {
        query = String(e.target.value || '')
        selected = 0
        renderResults()
      },
      onkeydown: onInputKeys,
    })
    results = h('div', { class: 'cmdk-results', role: 'listbox', id: 'cmdk-results' })
    overlay = h(
      'div',
      { class: 'cmdk-overlay', 'data-cmdk': '', ...overlayCloser(close) },
      h(
        'div',
        { class: 'cmdk-panel', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Quick search' },
        modalClose(close),
        input,
        results
      )
    )
    doc.body.appendChild(overlay)
    undoEscape = closeOnEscape(close, doc)
    unlockScroll = lockScroll(doc)
    input.focus()
    renderResults()
  }

  function close() {
    if (!overlay) return
    undoEscape()
    unlockScroll()
    overlay.remove()
    overlay = null
    input = null
    results = null
    flat = []
  }

  function onGlobalKey(/** @type {any} */ e) {
    if ((e.key !== 'k' && e.key !== 'K') || (!e.metaKey && !e.ctrlKey) || e.altKey) return
    /* The palette's own box is the one editor the shortcut works from, so a
       second ⌘K closes what the first one opened. */
    if (inEditor(e.target) && e.target !== input) return
    e.preventDefault()
    if (overlay) close()
    else open()
  }

  doc.addEventListener('keydown', onGlobalKey)
  return () => {
    doc.removeEventListener('keydown', onGlobalKey)
    close()
  }
}

/** @type {(() => void)|null} the palette of the CURRENT sign-in, if any */
let detach = null

/**
 * The real wiring, one call per auth change from `main.js`: mounts for a
 * signed-in uid, tears the previous palette down first so listeners and the
 * cached data never leak from one advisor's session into the next's, and
 * plain teardown on sign-out ('').
 *
 * @param {string} uid
 * @returns {() => void}
 */
export function attachCommandPalette(uid) {
  if (detach) {
    detach()
    detach = null
  }
  if (uid) detach = createCommandPalette({ load: () => loadWorkspace(uid), navigate: goTo })
  return () => {
    if (detach) {
      detach()
      detach = null
    }
  }
}
